import { zodResolver } from "@hookform/resolvers/zod";
import { FileUp, Save, Upload, X } from "lucide-react";
import { useCallback, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { z } from "zod";

import { useUploadDocument } from "../api/useKnowledgeApi";
import { useFileDrop } from "../hooks/useFileDrop";

import ModernPageLayout from "@/components/layout/ModernPageLayout";
import { Button } from "@/components/ui/v2/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/v2/card";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/v2/form";
import { Input } from "@/components/ui/v2/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/v2/select";
import { Textarea } from "@/components/ui/v2/textarea";
import useToast from "@/lib/hooks/useToast";

const CHUNK_TYPES = [
  { value: "domain-knowledge", label: "Domain Knowledge", description: "Core system behavior and architecture" },
  { value: "q-and-a", label: "Q&A", description: "Common questions and answers" },
  { value: "example", label: "Example", description: "Worked examples and sample scenarios" },
  { value: "procedure", label: "Procedure", description: "Step-by-step operational guides" },
];

const schema = z.object({
  fileName: z
    .string()
    .min(1, "File name is required")
    .max(200, "File name must be at most 200 characters")
    .regex(/^[\w\-. ]+$/, "Only letters, numbers, dashes, underscores, dots, and spaces"),
  title: z
    .string()
    .min(1, "Title is required")
    .max(300, "Title must be at most 300 characters")
    .regex(/^[\x20-\x7E]+$/, "Title may only contain ASCII characters (no accents, emoji, or special scripts)"),
  chunkType: z.string().min(1, "Type is required"),
  content: z.string().min(1, "Content is required"),
});

type FormValues = z.infer<typeof schema>;

function KnowledgeDocumentCreatePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { integrationName } = useParams<{ integrationName: string }>();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadMutation = useUploadDocument();

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      fileName: searchParams.get("fileName") ?? "",
      title: searchParams.get("title") ?? "",
      chunkType: "",
      content: searchParams.get("content") ?? "",
    },
  });

  const hasContent = form.watch("content").length > 0;
  const backHref = `/integrations/${integrationName}/chat`;

  const onSubmit = useCallback(
    (values: FormValues) => {
      uploadMutation.mutate(
        {
          fileName: values.fileName,
          content: values.content,
          title: values.title,
          chunkType: values.chunkType,
        },
        {
          onSuccess: () => {
            const normalized = values.fileName
              .trim()
              .replace(/\s/g, "-")
              .toLowerCase();
            const withExt = normalized.endsWith(".md")
              ? normalized
              : `${normalized}.md`;
            navigate(
              `/integrations/${integrationName}/chat/${encodeURIComponent(withExt)}`,
              { replace: true },
            );
          },
        },
      );
    },
    [uploadMutation, navigate, integrationName],
  );

  const handleFileLoad = useCallback(
    (file: File) => {
      const reader = new FileReader();
      reader.onload = () => {
        const fileContent = reader.result as string;
        form.setValue("fileName", file.name, { shouldValidate: true });
        form.setValue(
          "title",
          file.name.replace(/\.md$/, "").replace(/[-_]/g, " "),
          { shouldValidate: true },
        );
        form.setValue("content", fileContent, { shouldValidate: true });
        showToast(`Loaded ${file.name}`, "success");
      };
      reader.readAsText(file);
    },
    [form, showToast],
  );

  const handleFiles = useCallback(
    (files: FileList | File[]) => {
      const mdFiles = Array.from(files).filter((f) => f.name.endsWith(".md"));
      if (mdFiles.length === 0) {
        showToast("Only .md files are supported", "error");
        return;
      }
      handleFileLoad(mdFiles[0]);
    },
    [handleFileLoad, showToast],
  );

  const { isDragging, dropProps } = useFileDrop({
    onFileDrop: (file) => handleFileLoad(file),
  });

  const breadcrumbs = (
    <nav className="flex items-center gap-1.5 text-sm text-muted-foreground">
      <Link to="/integrations" className="hover:text-foreground">
        {t("common.integrations", { defaultValue: "Integrations" })}
      </Link>
      <span>&gt;</span>
      <Link to={`/integrations/${integrationName}`} className="hover:text-foreground">
        Captain
      </Link>
      <span>&gt;</span>
      <Link to={backHref} className="hover:text-foreground">
        {t("integrations.modules.names.chat", { defaultValue: "Chat" })}
      </Link>
      <span>&gt;</span>
      <span className="text-foreground">{t("captain.knowledge.createTitle")}</span>
    </nav>
  );

  return (
    <ModernPageLayout
      breadcrumbs={breadcrumbs}
      title={t("captain.knowledge.createTitle")}
      subtitle={t("captain.knowledge.createSubtitle")}
      backbutton={{ label: t("common.back"), href: backHref }}
    >
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} {...dropProps}>
          <div className="flex items-center justify-end gap-2 mb-6">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
            >
              <X className="mr-2 h-4 w-4" />
              {t("captain.knowledge.cancelButton")}
            </Button>
            <Button
              type="submit"
              size="sm"
              loading={uploadMutation.isPending}
            >
              <Save className="mr-2 h-4 w-4" />
              {t("captain.knowledge.createButton")}
            </Button>
          </div>

          {!hasContent && (
            <div
              className={`mb-6 flex flex-col items-center justify-center rounded-xl border-2 border-dashed p-10 transition-all ${
                isDragging
                  ? "border-primary bg-primary/5 scale-[1.01]"
                  : "border-border hover:border-muted-foreground/50"
              }`}
            >
              <div className="flex flex-col items-center gap-3 text-center">
                <div className="rounded-full bg-muted p-4">
                  <FileUp className="h-8 w-8 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-sm font-medium">
                    {isDragging ? t("captain.knowledge.dropHere") : t("captain.knowledge.startFromFile")}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t("captain.knowledge.dropOrBrowse")}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="mr-2 h-4 w-4" />
                  {t("captain.knowledge.browseFiles")}
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".md"
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files) handleFiles(e.target.files);
                    e.target.value = "";
                  }}
                />
              </div>
            </div>
          )}

          {hasContent && isDragging && (
            <div className="mb-6 flex items-center justify-center rounded-xl border-2 border-dashed border-primary bg-primary/5 p-8 transition-all">
              <div className="flex items-center gap-2 text-muted-foreground">
                <FileUp className="h-5 w-5" />
                <p className="text-sm">{t("captain.knowledge.dropToReplace")}</p>
              </div>
            </div>
          )}

          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t("captain.knowledge.detailsCard")}</CardTitle>
                <CardDescription>
                  {t("captain.knowledge.detailsCardDescription")}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 items-start gap-4">
                  <FormField
                    control={form.control}
                    name="fileName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("captain.knowledge.fileNameLabel")}</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder="indeed-troubleshooting.md"
                          />
                        </FormControl>
                        <div className="min-h-5">
                          <FormMessage />
                        </div>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("captain.knowledge.titleLabel")}</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder="Indeed Troubleshooting Guide"
                          />
                        </FormControl>
                        <div className="min-h-5">
                          <FormMessage />
                        </div>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="chunkType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("captain.knowledge.typeLabel")}</FormLabel>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder={t("captain.knowledge.selectType")} />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {CHUNK_TYPES.map((ct) => (
                              <SelectItem key={ct.value} value={ct.value}>
                                <div className="flex flex-col">
                                  <span>{ct.label}</span>
                                  <span className="text-xs text-muted-foreground">
                                    {ct.description}
                                  </span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <div className="min-h-5">
                          <FormMessage />
                        </div>
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-base">{t("captain.knowledge.contentCard")}</CardTitle>
                  <CardDescription>
                    {t("captain.knowledge.contentCardDescription")}
                  </CardDescription>
                </div>
                {hasContent && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload className="mr-2 h-4 w-4" />
                    {t("captain.knowledge.replaceFromFile")}
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                <FormField
                  control={form.control}
                  name="content"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Textarea
                          {...field}
                          placeholder={"# Document Title\n\nWrite your knowledge document here...\n\n## Section\n\nUse markdown formatting for structure."}
                          className="min-h-[500px] font-mono text-sm"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>
          </div>
        </form>
      </FormProvider>
    </ModernPageLayout>
  );
}

export default KnowledgeDocumentCreatePage;
